/**
 * Audit Trail API
 *
 * Phase 16: Enterprise Audit Trail System
 * Provides functions for security alerts, analytics, and audit log management.
 */

import { api } from './api'

// ============================================================================
// Types (camelCase to match API response transformer)
// ============================================================================

export interface AlertConfiguration {
  id: string
  alertType: string
  isEnabled: boolean
  thresholdCount: number | null
  thresholdWindowMinutes: number | null
  notifyAllAdmins: boolean
  additionalEmails: string[] | null
  createdAt: string
  updatedAt: string
}

export interface AlertConfigurationUpdate {
  is_enabled?: boolean
  threshold_count?: number | null
  threshold_window_minutes?: number | null
  notify_all_admins?: boolean
  additional_emails?: string[] | null
}

export interface UserSummary {
  id: string
  email: string
  fullName: string
}

export interface SecurityAlert {
  id: string
  alertType: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  userId: string | null
  ipAddress: string | null
  userAgent: string | null
  details: Record<string, unknown> | null
  isAcknowledged: boolean
  acknowledgedBy: string | null
  acknowledgedAt: string | null
  createdAt: string
  user: UserSummary | null
  acknowledger: UserSummary | null
}

export interface SecurityAlertListResponse {
  data: SecurityAlert[]
  total: number
  page: number
  pageSize: number
  unacknowledgedCount: number
}

export interface AnalyticsEvent {
  id: string
  userId: string | null
  sessionId: string | null
  eventType: string
  pagePath: string | null
  studyId: string | null
  moduleKey: string | null
  eventMetadata: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  createdAt: string
}

export interface AnalyticsSummary {
  totalEvents: number
  pageViews: number
  uniqueUsers: number
  topPages: Array<{ page: string; views: number }>
  eventsByType: Record<string, number>
  eventsByDay: Array<{ date: string; count: number }>
}

export interface AuditLogEntry {
  id: number
  userId: string | null
  userEmail: string | null
  action: string
  resourceType: string | null
  resourceId: string | null
  details: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  status: string  // 'success', 'failure', 'error'
  studyId: string | null
  createdAt: string
}

export interface AuditLogListResponse {
  data: AuditLogEntry[]
  total: number
  page: number
  pageSize: number
}

export interface AuditLogStats {
  totalLogs: number
  successfulActions: number
  failedActions: number
  uniqueUsers: number
  actionsByType: Record<string, number>
  logsByDay: Array<{ date: string; count: number }>
  topUsers: Array<{ email: string; actions: number }>
}

// ============================================================================
// Alert Configuration API
// ============================================================================

export async function fetchAlertConfigurations(): Promise<AlertConfiguration[]> {
  const response = await api.get<AlertConfiguration[]>('/audit/alert-config')
  return response.data
}

export async function updateAlertConfiguration(
  alertType: string,
  data: AlertConfigurationUpdate
): Promise<AlertConfiguration> {
  const response = await api.put<AlertConfiguration>(`/audit/alert-config/${alertType}`, data)
  return response.data
}

export async function sendTestAlert(alertType: string): Promise<{ status: string; message: string }> {
  const response = await api.post<{ status: string; message: string }>(`/audit/alert-config/test?alert_type=${alertType}`)
  return response.data
}

// ============================================================================
// Security Alerts API
// ============================================================================

export interface SecurityAlertFilters {
  alert_type?: string
  severity?: string
  is_acknowledged?: boolean
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
}

export async function fetchSecurityAlerts(
  filters: SecurityAlertFilters = {}
): Promise<SecurityAlertListResponse> {
  const params = new URLSearchParams()
  if (filters.alert_type) params.append('alert_type', filters.alert_type)
  if (filters.severity) params.append('severity', filters.severity)
  if (filters.is_acknowledged !== undefined) params.append('is_acknowledged', String(filters.is_acknowledged))
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)
  params.append('page', String(filters.page || 1))
  params.append('page_size', String(filters.page_size || 50))

  const response = await api.get<SecurityAlertListResponse>(`/audit/security-alerts?${params.toString()}`)
  return response.data
}

export async function fetchActiveAlerts(): Promise<SecurityAlert[]> {
  const response = await api.get<SecurityAlert[]>('/audit/security-alerts/active')
  return response.data
}

export async function syncKeycloakEvents(): Promise<{
  status: string
  reason?: string
  events_found?: number
  unique_ips?: number
  alerts_created?: number
}> {
  const response = await api.post<{
    status: string
    reason?: string
    events_found?: number
    unique_ips?: number
    alerts_created?: number
  }>('/audit/security-alerts/sync-keycloak')
  return response.data
}

export async function acknowledgeAlert(
  alertId: string,
  note?: string
): Promise<SecurityAlert> {
  const response = await api.post<SecurityAlert>(`/audit/security-alerts/${alertId}/acknowledge`, note ? { note } : {})
  return response.data
}

// ============================================================================
// Analytics API
// ============================================================================

export interface AnalyticsFilters {
  start_date?: string
  end_date?: string
  study_id?: string
}

export async function trackAnalyticsEvent(data: {
  event_type: string
  page_path?: string
  study_id?: string
  module_key?: string
  event_metadata?: Record<string, unknown>
  session_id?: string
}): Promise<AnalyticsEvent> {
  const response = await api.post<AnalyticsEvent>('/audit/analytics/events', data)
  return response.data
}

export async function fetchAnalyticsSummary(
  filters: AnalyticsFilters = {}
): Promise<AnalyticsSummary> {
  const params = new URLSearchParams()
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)
  if (filters.study_id) params.append('study_id', filters.study_id)

  const query = params.toString()
  const response = await api.get<AnalyticsSummary>(`/audit/analytics/summary${query ? `?${query}` : ''}`)
  return response.data
}

// ============================================================================
// Audit Logs API
// ============================================================================

export interface AuditLogFilters {
  start_date?: string
  end_date?: string
  user_id?: string
  action?: string
  resource_type?: string
  study_id?: string
  ip_address?: string
  success?: boolean
  page?: number
  page_size?: number
}

export async function fetchAuditLogs(
  filters: AuditLogFilters = {}
): Promise<AuditLogListResponse> {
  const params = new URLSearchParams()
  if (filters.start_date) params.append('start_date', filters.start_date)
  if (filters.end_date) params.append('end_date', filters.end_date)
  if (filters.user_id) params.append('user_id', filters.user_id)
  if (filters.action) params.append('action', filters.action)
  if (filters.resource_type) params.append('resource_type', filters.resource_type)
  if (filters.study_id) params.append('study_id', filters.study_id)
  if (filters.ip_address) params.append('ip_address', filters.ip_address)
  if (filters.success !== undefined) params.append('success', String(filters.success))
  params.append('page', String(filters.page || 1))
  params.append('page_size', String(filters.page_size || 50))

  const response = await api.get<AuditLogListResponse>(`/audit/logs/enhanced?${params.toString()}`)
  return response.data
}

export async function fetchAuditLogStats(
  startDate?: string,
  endDate?: string
): Promise<AuditLogStats> {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)

  const query = params.toString()
  const response = await api.get<AuditLogStats>(`/audit/logs/stats${query ? `?${query}` : ''}`)
  return response.data
}

// ============================================================================
// Helpers
// ============================================================================

export function getAlertTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    failed_login: 'Failed Login',
    admin_change: 'Admin Change',
    bulk_export: 'Bulk Export',
  }
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function getSeverityColor(severity: string): string {
  const colors: Record<string, string> = {
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  }
  return colors[severity] || 'bg-gray-100 text-gray-800'
}
